import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Link from '@mui/material/Link';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import {
  OrganizationSchema,
  SupplierInvoiceDetailSchema,
  SupplierInvoiceSchema,
  SupplierPayableBalanceSchema,
  type SupplierDto,
} from '@taqon/contracts';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Link as RouterLink, useSearchParams } from 'react-router';
import { getData, getPage, newIdempotencyKey, postJson } from '../../shared/api/client';
import { hasCapability, useSession } from '../../shared/session/session';
import { StatusChip } from '../../shared/ui/forms';
import { EmptyState, ErrorState, LoadingState, PageHeader, Section } from '../../shared/ui/page';
import { refreshAfterPosting } from './cache';
import { RefusalState } from './refusal';
import {
  businessToday,
  emptyLine,
  lineProblems,
  lineRequests,
  LinesEditor,
  linesComplete,
  type LineDraft,
} from './line-editor';
import { SupplierPicker } from './lookups';

/**
 * Supplier invoice capture and posting (FRD-FIN-070 to 073, FIN-077; ERD §5.9).
 *
 * Capture takes any number of lines (see `line-editor.tsx` for the accounts and dimensions).
 *
 * Creating carries an `Idempotency-Key` and posting carries `If-Match` (SDD §7.3, §7.5). Actions
 * are shown only with their permission; the API remains the enforcement point (SDD §22.4).
 */
export function SupplierInvoicesPage() {
  const session = useSession();
  const queryClient = useQueryClient();
  const canCreate = hasCapability(session.data, 'finance.supplier_invoice:create');
  const canPost = hasCapability(session.data, 'finance.supplier_invoice:post');
  const [supplier, setSupplier] = useState<SupplierDto | null>(null);
  // The list's existing API filters, read from the URL, so a dashboard figure opens exactly the
  // invoices it counts (FRD-RPT-007): `?status=draft|posted`, `?open=true` (outstanding), and a
  // campaign's spend: `?campaign_id=` with `?invoice_date_from=` / `?invoice_date_to=` (CLR-18).
  const [urlParams] = useSearchParams();
  const listFilters = new URLSearchParams({ pageSize: '50', sort: '-document_no' });
  const status = urlParams.get('status') ?? '';
  const open = urlParams.get('open') === 'true';
  const campaignId = urlParams.get('campaign_id') ?? '';
  const dateFrom = urlParams.get('invoice_date_from') ?? '';
  const dateTo = urlParams.get('invoice_date_to') ?? '';
  // A supplier's invoices, from the supplier outstanding report (FRD-FIN-132, FIN-137).
  const supplierFilter = urlParams.get('supplier') ?? '';
  if (supplierFilter !== '') listFilters.set('filter[supplier_id]', supplierFilter);
  if (status !== '') listFilters.set('filter[status]', status);
  if (open) listFilters.set('filter[open]', 'true');
  if (campaignId !== '') listFilters.set('filter[campaign_id]', campaignId);
  if (dateFrom !== '') listFilters.set('filter[invoice_date_from]', dateFrom);
  if (dateTo !== '') listFilters.set('filter[invoice_date_to]', dateTo);
  const filtered =
    status !== '' ||
    open ||
    campaignId !== '' ||
    dateFrom !== '' ||
    dateTo !== '' ||
    supplierFilter !== '';
  const filterText = [
    supplierFilter === '' ? null : 'of one supplier',
    status === '' ? null : `status ${status}`,
    open ? 'outstanding' : null,
    campaignId === '' ? null : 'lines naming the campaign',
    dateFrom === '' ? null : `dated from ${dateFrom}`,
    dateTo === '' ? null : `dated to ${dateTo}`,
  ]
    .filter((part) => part !== null)
    .join(', ');

  const invoices = useQuery({
    queryKey: ['supplier-invoices', listFilters.toString()],
    queryFn: () =>
      getPage(`/api/v1/finance/supplier-invoices?${listFilters.toString()}`, SupplierInvoiceSchema),
  });
  const balance = useQuery({
    queryKey: ['payable-balance', supplier?.id],
    enabled: supplier !== null,
    queryFn: () =>
      getData(
        `/api/v1/finance/suppliers/${supplier?.id ?? ''}/payable-balance`,
        SupplierPayableBalanceSchema,
      ),
  });

  const post = useMutation({
    mutationFn: (invoice: { id: string; version: number }) =>
      postJson(
        `/api/v1/finance/supplier-invoices/${invoice.id}/post`,
        {},
        SupplierInvoiceSchema,
        session.data?.csrfToken ?? '',
        { expectedVersion: invoice.version },
      ),
    onSuccess: () => refreshAfterPosting(queryClient),
  });

  return (
    <Stack spacing={3}>
      <PageHeader
        title="Supplier invoices"
        description="Capture supplier invoices as drafts, then post them to the ledger."
      />
      {canCreate && <CaptureInvoice supplier={supplier} onSupplierChange={setSupplier} />}

      {supplier !== null && (
        <Section title="Outstanding payable for this supplier">
          <Box
            component="section"
            role="region"
            aria-label="Outstanding payable for this supplier"
            sx={{ p: 2.5 }}
          >
            {balance.isError && <ErrorState message={balance.error.message} />}
            <Typography variant="h3" component="p">
              {balance.data?.outstandingAmount ?? '—'}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Across {balance.data?.invoiceCount ?? 0} posted invoice(s). Derived from the posted
              documents, not a stored figure.
            </Typography>
          </Box>
        </Section>
      )}

      {post.isError && <RefusalState error={post.error} />}
      {invoices.isError && <ErrorState message={invoices.error.message} />}
      <Section title="Invoices">
        {filtered && (
          <Alert
            severity="info"
            sx={{ m: 2 }}
            action={
              <Button component={RouterLink} to="/supplier-invoices" size="small" color="inherit">
                Show all
              </Button>
            }
          >
            Showing invoices filtered by {filterText}
            {invoices.data === undefined ? '' : ` (${String(invoices.data.total)})`}.
          </Alert>
        )}
        {invoices.isPending && <LoadingState label="Loading invoices" />}
        {invoices.data !== undefined && invoices.data.items.length === 0 && (
          <EmptyState title={filtered ? 'No invoices match the filter' : 'No invoices yet'} />
        )}
        {invoices.data !== undefined && invoices.data.items.length > 0 && (
          <Table size="small" aria-label="Supplier invoices">
            <TableHead>
              <TableRow>
                <TableCell>Document</TableCell>
                <TableCell>Supplier reference</TableCell>
                <TableCell>Date</TableCell>
                <TableCell align="right">Net</TableCell>
                <TableCell align="right">VAT</TableCell>
                <TableCell align="right">Gross</TableCell>
                <TableCell align="right">Outstanding</TableCell>
                <TableCell>Payment</TableCell>
                <TableCell>Status</TableCell>
                {canPost && <TableCell align="right">Action</TableCell>}
              </TableRow>
            </TableHead>
            <TableBody>
              {invoices.data.items.map((invoice) => (
                <TableRow key={invoice.id} hover>
                  <TableCell sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>
                    <Link component={RouterLink} to={`/supplier-invoices/${invoice.id}`}>
                      {invoice.documentNo}
                    </Link>
                  </TableCell>
                  <TableCell>{invoice.supplierInvoiceNumber}</TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>{invoice.supplierInvoiceDate}</TableCell>
                  <TableCell align="right">{invoice.netTotal}</TableCell>
                  <TableCell align="right">{invoice.vatTotal}</TableCell>
                  <TableCell align="right">{invoice.grossTotal}</TableCell>
                  <TableCell align="right">{invoice.outstandingAmount}</TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>
                    {invoice.paymentStatus.replace('_', ' ')}
                  </TableCell>
                  <TableCell>
                    <StatusChip status={invoice.status} />
                  </TableCell>
                  {canPost && (
                    <TableCell align="right">
                      {invoice.status === 'draft' && (
                        <Button
                          size="small"
                          variant="outlined"
                          disabled={post.isPending}
                          onClick={() => {
                            post.mutate({ id: invoice.id, version: invoice.version });
                          }}
                        >
                          Post
                        </Button>
                      )}
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Section>
    </Stack>
  );
}

function CaptureInvoice({
  supplier,
  onSupplierChange,
}: {
  supplier: SupplierDto | null;
  onSupplierChange: (supplier: SupplierDto | null) => void;
}) {
  const session = useSession();
  const queryClient = useQueryClient();
  const [invoiceNumber, setInvoiceNumber] = useState('');
  const [invoiceDate, setInvoiceDate] = useState(() =>
    businessToday(session.data?.businessTimezone),
  );
  const [dueDate, setDueDate] = useState('');
  const [description, setDescription] = useState('');
  const [lines, setLines] = useState<LineDraft[]>([emptyLine(1)]);

  // One legal entity (D2), so the invoice's organization is simply the one that exists.
  const organization = useQuery({
    queryKey: ['organization'],
    queryFn: () => getData('/api/v1/organization/organization', OrganizationSchema),
  });
  const problems = lineProblems(lines);
  const complete =
    organization.data !== undefined &&
    supplier !== null &&
    invoiceNumber.trim() !== '' &&
    invoiceDate !== '' &&
    linesComplete(lines);

  const create = useMutation({
    mutationFn: () =>
      postJson(
        '/api/v1/finance/supplier-invoices',
        {
          supplierId: supplier?.id ?? '',
          supplierInvoiceNumber: invoiceNumber.trim(),
          supplierInvoiceDate: invoiceDate,
          taxDate: invoiceDate,
          ...(dueDate !== '' ? { dueDate } : {}),
          ...(description.trim() !== '' ? { description: description.trim() } : {}),
          organizationId: organization.data?.id ?? '',
          lines: lineRequests(lines),
        },
        SupplierInvoiceDetailSchema,
        session.data?.csrfToken ?? '',
        // One key per deliberate submission: a repeat returns the original document.
        { idempotencyKey: newIdempotencyKey() },
      ),
    onSuccess: async () => {
      setInvoiceNumber('');
      setDescription('');
      setDueDate('');
      // A fresh key, so the pickers of the new line start empty.
      setLines((current) => [
        emptyLine(current.reduce((max, line) => Math.max(max, line.key), 0) + 1),
      ]);
      await refreshAfterPosting(queryClient);
    },
  });

  return (
    <Section title="Capture an invoice">
      <Stack
        spacing={2.5}
        sx={{ p: 2.5 }}
        component="form"
        aria-label="Capture an invoice"
        noValidate
        onSubmit={(event) => {
          event.preventDefault();
          if (complete && problems.length === 0 && !create.isPending) create.mutate();
        }}
      >
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
          <Box sx={{ minWidth: 280, flexGrow: 1 }}>
            <SupplierPicker value={supplier} onChange={onSupplierChange} />
          </Box>
          <TextField
            size="small"
            label="Supplier's invoice number"
            value={invoiceNumber}
            required
            onChange={(e) => {
              setInvoiceNumber(e.target.value);
            }}
          />
          <TextField
            size="small"
            type="date"
            label="Invoice date"
            required
            value={invoiceDate}
            slotProps={{ inputLabel: { shrink: true } }}
            onChange={(e) => {
              setInvoiceDate(e.target.value);
            }}
          />
          <TextField
            size="small"
            type="date"
            label="Due date"
            value={dueDate}
            slotProps={{ inputLabel: { shrink: true } }}
            onChange={(e) => {
              setDueDate(e.target.value);
            }}
          />
        </Stack>
        <TextField
          size="small"
          label="Description"
          value={description}
          onChange={(e) => {
            setDescription(e.target.value);
          }}
        />

        <LinesEditor lines={lines} onChange={setLines} withCampaign withTaxOverride />

        {problems.length > 0 && (
          <Alert severity="warning" variant="outlined">
            {problems.map((p) => (
              <div key={p}>{p}</div>
            ))}
          </Alert>
        )}
        {create.isError && <RefusalState error={create.error} />}
        {create.isSuccess && (
          <Alert severity="success" variant="outlined">
            Invoice{' '}
            <Link component={RouterLink} to={`/supplier-invoices/${create.data.invoice.id}`}>
              {create.data.invoice.documentNo}
            </Link>{' '}
            captured as a draft — net {create.data.invoice.netTotal}, VAT{' '}
            {create.data.invoice.vatTotal}, gross {create.data.invoice.grossTotal}.
          </Alert>
        )}
        <Box>
          <Button
            type="submit"
            variant="contained"
            disabled={!complete || problems.length > 0 || create.isPending}
          >
            {create.isPending ? 'Capturing…' : 'Capture invoice'}
          </Button>
        </Box>
      </Stack>
    </Section>
  );
}
